import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ArrowUpRight, Menu as MenuIcon } from "lucide-react";
import { apiGet, apiPost, apiErr, money, dateFmt, timeFmt, safeUrl, mediaUrl } from "./api";
import OwnerDashboard from "./components/Owner";
import { Block } from "./components/Blocks";

const Ctx = createContext(null);
export const useSite = () => useContext(Ctx);

const qp = () => new URLSearchParams(window.location.search);
const isDraft = () => qp().get("draft") === "1";
export const isPreview = () => qp().get("preview") === "1";

function applyTheme(theme) {
  if (!theme) return;
  const r = document.documentElement;
  if (theme.ink) r.style.setProperty("--ink", theme.ink);
  if (theme.gold) r.style.setProperty("--gold", theme.gold);
  if (theme.paper) r.style.setProperty("--paper", theme.paper);
  if (theme.font) r.style.setProperty("--serif", `${theme.font},Georgia,serif`);
}

// Per-section color themes. Consulting/home use the owner-editable theme; other areas get fixed palettes.
const AREA_THEMES = {
  insurance: { ink: "#1f3b45", gold: "#b06a3f", paper: "#eef3f2", white: "#ffffff" },
  social: { ink: "#0a0a0c", gold: "#d4af37", paper: "#0a0a0c", white: "#121216" },
  about: { ink: "#4a3220", gold: "#c0693f", paper: "#fbf5ec", white: "#ffffff" },
};
function applyAreaTheme(area, theme) {
  const r = document.documentElement;
  r.setAttribute("data-area", area || "home");
  const p = AREA_THEMES[area];
  if (p) {
    r.style.setProperty("--ink", p.ink);
    r.style.setProperty("--gold", p.gold);
    r.style.setProperty("--paper", p.paper);
    r.style.setProperty("--white", p.white);
    r.style.setProperty("--serif", "Georgia,'Times New Roman',serif");
  } else {
    r.style.setProperty("--white", "#fffdf8");
    applyTheme(theme);
  }
}

// click-to-edit bridge: in preview mode, clicking a [data-edit] element tells the parent editor
function useEditBridge() {
  useEffect(() => {
    if (!isPreview()) return;
    const onClick = (e) => {
      const img = e.target.closest("[data-edit-img]");
      if (img) {
        e.preventDefault(); e.stopPropagation();
        window.parent.postMessage({ type: "mk-edit-image", key: img.getAttribute("data-edit-img"), label: img.getAttribute("data-edit-label") }, "*");
        return;
      }
      const el = e.target.closest("[data-edit]");
      if (el) {
        e.preventDefault();
        window.parent.postMessage({ type: "mk-edit", key: el.getAttribute("data-edit") }, "*");
      }
    };
    document.body.classList.add("mk-preview");
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}

export const Ed = ({ k, children, as = "span", ...rest }) => {
  const Tag = as;
  const extra = isPreview() ? { "data-edit": k, style: { outline: "1px dashed #96735055", cursor: "pointer", ...(rest.style || {}) } } : {};
  return <Tag {...rest} {...extra}>{children}</Tag>;
};
// Editable image slot: in preview mode a click opens the editor's image tool for `k`.
export const EdImg = ({ k, label, children, as = "div", ...rest }) => {
  const Tag = as;
  const extra = isPreview() ? { "data-edit-img": k, "data-edit-label": label, className: `${rest.className || ""} mk-img-edit`.trim(), title: `Click to change: ${label}` } : {};
  return <Tag {...rest} {...extra}>{children}</Tag>;
};

// Internal routes stay relative ("/online") and render as router links; anything else is an external https link.
export const isInternalUrl = (u) => typeof u === "string" && u.startsWith("/") && !u.startsWith("//");
export const SmartLink = ({ to, children, ...rest }) => isInternalUrl(to)
  ? <Link to={to} {...rest}>{children}</Link>
  : <a href={safeUrl(to)} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>;

// Append tracking so a shared Calendly event can still be attributed to the section that sent the visitor.
const bookingLink = (base, section, extra = {}) => {
  if (!base) return "";
  try {
    const u = new URL(base);
    u.searchParams.set("utm_source", section);
    Object.entries(extra).forEach(([k, v]) => v && u.searchParams.set(k, v));
    return u.href;
  } catch { return ""; }
};

/* ------------------------------------------------------------------ per-route titles & sharing metadata */
const routeMetaKey = (p) => p === "/" ? "home" : p.startsWith("/life-insurance") ? "insurance" : p.startsWith("/online") ? "social"
  : p.startsWith("/about") ? "about" : (p.startsWith("/packages") || p.startsWith("/purchase")) ? "packages" : p.startsWith("/book") ? "book"
  : p.startsWith("/contact") ? "contact" : p.startsWith("/start") ? "start" : p.startsWith("/services") ? "services"
  : ["/login", "/portal", "/admin"].some((x) => p.startsWith(x)) ? "portal"
  : p.startsWith("/privacy") ? "privacy" : p.startsWith("/terms") ? "terms" : p.startsWith("/cancellation") ? "cancellation" : "consulting";
function setMeta(selector, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}
function useRouteMeta(site, pathname) {
  useEffect(() => {
    const seo = site.seo || {}, pages = seo.pages || {}, key = routeMetaKey(pathname);
    const custom = (site.customPages || []).find((p) => p.path === pathname);
    const m = custom ? (custom.seo || {}) : key === "home" ? {} : (pages[key] || {});
    const title = m.title || (custom ? `${custom.title} | Michael Ephriam Khairi` : "") || seo.title || "Michael Ephriam Khairi";
    const desc = m.description || seo.description || "";
    const img = seo.image ? (seo.image.startsWith("/api/") ? mediaUrl(seo.image) : window.location.origin + seo.image) : `${window.location.origin}/mk-logo.png`;
    document.title = title;
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', title); setMeta('meta[property="og:description"]', desc);
    setMeta('meta[property="og:type"]', "website"); setMeta('meta[property="og:url"]', window.location.origin + pathname);
    setMeta('meta[property="og:image"]', img);
    setMeta('meta[name="twitter:card"]', "summary"); setMeta('meta[name="twitter:title"]', title); setMeta('meta[name="twitter:description"]', desc);
  }, [site.seo, site.customPages, pathname]);
}

/* ------------------------------------------------------------------ shell */
const AREA_MENUS = {
  consulting: { label: "Consulting", items: [["Overview", "/consulting"], ["Services", "/services"], ["Free consult", "/book"], ["Contact", "/contact"]] },
  insurance: { label: "Life insurance", items: [["Overview", "/life-insurance"], ["Ask a question", "/life-insurance#ask"], ["Coverage options", "/life-insurance/options"], ["Schedule", "/life-insurance/book"]] },
  social: { label: "Social", items: [["Overview", "/online"], ["Posts", "/online#posts"], ["Videos", "/online#videos"], ["Membership", "/online#membership"], ["Support", "/online#support"], ["Social links", "/online#links"]] },
  about: { label: "About me", items: [["Overview", "/about"], ["My story", "/about#story"], ["Photos", "/about#photos"], ["Connect", "/about#connect"]] },
};
// Owner-editable menus (site.menus) + published custom pages, falling back to the built-in defaults.
function useMenus() {
  const { site } = useSite();
  return useMemo(() => {
    const m = site.menus || {}, pages = site.customPages || [];
    const out = {};
    for (const [k, def] of Object.entries(AREA_MENUS)) {
      const base = Array.isArray(m[k]) && m[k].length ? m[k].filter((x) => !x.hidden && x.url) : def.items.map(([label, url]) => ({ label, url }));
      const extra = pages.filter((p) => p.section === k && p.inMenu).map((p) => ({ label: p.menuLabel || p.title, url: p.path }));
      out[k] = { label: def.label, items: [...base, ...extra].map((x) => [x.label, x.url]) };
    }
    return out;
  }, [site.menus, site.customPages]);
}
function currentArea(pathname) {
  if (pathname.startsWith("/life-insurance")) return "insurance";
  if (pathname.startsWith("/online")) return "social";
  if (pathname.startsWith("/about")) return "about";
  if (["/consulting", "/services", "/book", "/contact", "/start", "/portal", "/admin", "/login", "/purchase"].some((p) => pathname.startsWith(p))) return "consulting";
  return null;
}
function Header() {
  const { site, me } = useSite();
  const menus = useMenus();
  const [open, setOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const loc = useLocation();
  const brand = site.brand || {};
  const area = currentArea(loc.pathname);
  useEffect(() => { setOpen(false); setExploreOpen(false); }, [loc.pathname]);
  const rightItems = area && menus[area] ? menus[area].items : [];
  return (
    <header className="site">
      <div className="wrap nav">
        <div className="row" style={{ gap: 18 }}>
          <div className="explore-wrap">
            <button className="explore-btn" onClick={() => setExploreOpen((o) => !o)} aria-expanded={exploreOpen} data-testid="explore-menu-btn">
              <MenuIcon size={16} /> Explore
            </button>
            {exploreOpen && <div className="explore-pop" data-testid="explore-menu">
              {Object.entries(AREA_MENUS).map(([k, m]) => (
                <Link key={k} to={m.items[0][1]} className={area === k ? "active" : ""} data-testid={`explore-${k}`}>{m.label}</Link>))}
            </div>}
          </div>
          <Link className="brand" to="/" aria-label="Michael Ephriam Khairi, home" data-testid="brand-home-link">
            <img className="brand-logo" src={mediaUrl(brand.logoUrl) || "/mk-logo.png"} alt={brand.logoAlt || "MK monogram logo"} />
            <span className="brand-copy">{"Michael Ephriam\nKhairi"}</span>
          </Link>
        </div>
        {area && <button className="menu-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="menu-toggle">Menu</button>}
        <nav className={`navlinks${open ? " open" : ""}`} aria-label={`${area || "Main"} navigation`}>
          {rightItems.map(([label, url]) => (
            <Link key={url} to={url} className={loc.pathname === url || (url !== "/consulting" && url.startsWith("/") && !url.includes("#") && loc.pathname.startsWith(url)) ? "active" : ""} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}>{label}</Link>
          ))}
          {area === "consulting" && <Link to={me.role === "admin" ? "/admin" : me.role ? "/portal" : "/login"} data-testid="nav-account">{me.role === "admin" ? "Owner dashboard" : "Client portal"}</Link>}
          {!area && <>
            <Link to="/consulting" data-testid="nav-consulting">Consulting</Link>
            <Link to="/life-insurance" data-testid="nav-insurance">Life insurance</Link>
            <Link to="/online" data-testid="nav-online">Social</Link>
            <Link to="/about" data-testid="nav-about">About me</Link>
          </>}
        </nav>
      </div>
    </header>
  );
}
/* ------------------------------------------------------------------ owner-created pages */
export const findCustomPage = (site, section, slug) => {
  const pages = site.customPages || [];
  const i = pages.findIndex((p) => p.section === section && p.slug === slug);
  return i >= 0 ? { page: pages[i], idx: i } : null;
};
function CustomPage({ section }) {
  const { site } = useSite();
  const { slug } = useParams();
  const hit = findCustomPage(site, section, slug);
  if (!hit) return <NotFound />;
  const { page: p, idx } = hit, k = `customPages.${idx}`, h = p.hero || {};
  return (<>
    <div className="page-head"><div className="wrap">
      <Ed k={`${k}.hero.eyebrow`} as="p" className="eyebrow">{h.eyebrow}</Ed><Ed k={`${k}.hero.title`} as="h1" data-testid="custom-page-title">{h.title || p.title}</Ed>
      {(h.lead || isPreview()) && <Ed k={`${k}.hero.lead`} as="p" className="lead">{h.lead}</Ed>}
    </div></div>
    <div className={`wrap content-bottom custom-page${["blank", "article", "faq"].includes(p.template) ? " narrow" : ""}`} data-testid={`custom-page-${p.slug}`}>
      {(h.image || isPreview()) && <EdImg k={`${k}.hero.image`} label="Hero image" as="figure" className={`cp-hero${h.image ? "" : " empty"}`}>
        {h.image ? <img src={mediaUrl(h.image) || h.image} alt={h.alt || ""} /> : <span>Click to add a hero photo</span>}</EdImg>}
      {(p.blocks || []).map((b, i) => <Block key={b.id || i} b={b} k={`${k}.blocks.${i}`} />)}
    </div>
  </>);
}

function Footer() {
  const { site, settings } = useSite();
  const brand = site.brand || {};
  const loc = useLocation();
  const area = currentArea(loc.pathname);
  return (
    <footer>
      <div className="wrap footer">
        <div className="footer-top">
          <div>
            <Link className="brand footer-brand" to="/" data-testid="footer-brand-link"><img className="brand-logo" src={mediaUrl(brand.logoUrl) || "/mk-logo.png"} alt="" />
              <span className="brand-copy">{area === "consulting" ? settings.name : "Michael Ephriam Khairi"}</span></Link>
          </div>
          <div className="footer-links">
            <Link to="/consulting">Consulting</Link><Link to="/life-insurance">Life insurance</Link>
            <Link to="/online">Social</Link><Link to="/about">About me</Link><Link to="/contact">Contact</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} Michael Ephriam Khairi</span>
          <div className="row">
            <Link to="/privacy">Privacy</Link><Link to="/terms">Service terms</Link>
            <Link to="/cancellation">Cancellations</Link><Link to="/admin">Owner login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

const PageHead = ({ eyebrow, title, lead }) => (
  <div className="page-head"><div className="wrap">
    <p className="eyebrow">{eyebrow}</p><h1>{title}</h1>{lead && <p className="lead">{lead}</p>}
  </div></div>
);

/* ------------------------------------------------------------------ consulting home */
function ConsultingHome() {
  const { site, settings, catalog } = useSite();
  const services = catalog.services || [];
  const h = site.hero || {}, ss = site.servicesSection || {}, ab = site.aboutSection || {};
  const ov = site.serviceOverrides || {};
  const sv = (s) => ({ ...s, ...(ov[s.id] || {}) });
  return (
    <>
      <div className="wrap"><section className="hero">
        <div>
          <Ed k="hero.eyebrow" as="p" className="eyebrow">{h.eyebrow}</Ed>
          <Ed k="hero.title" as="h1" data-testid="hero-title">{h.title}</Ed>
          <Ed k="hero.lead" as="p" className="lead">{h.lead}</Ed>
          <div className="row actions">
            <SmartLink to={h.primaryUrl || "/book"} className="button" data-testid="hero-primary-cta"><Ed k="hero.primaryLabel">{h.primaryLabel}</Ed> <ArrowUpRight size={15} /></SmartLink>
            <SmartLink to={h.secondaryUrl || "/services"} className="text-link"><Ed k="hero.secondaryLabel">{h.secondaryLabel}</Ed></SmartLink>
          </div>
          <Ed k="hero.note" as="p" className="hero-note">{h.note}</Ed>
        </div>
        <div className="hero-art" aria-hidden="true">
          <EdImg k="brand.logoUrl" label="MK logo" as="img" className="hero-watermark" src={mediaUrl((site.brand || {}).logoUrl) || "/mk-logo.png"} alt="" />
          <div className="art-rule" />
          <p className="art-title">{h.artTitle}</p>
          <div className="art-card"><span className="circle"><ArrowUpRight size={16} /></span>
            <div><strong>{h.cardTitle}</strong><p>{h.cardText}</p></div></div>
        </div>
      </section>
        <div className="strip">{(site.strip || []).map((s, i) => <span key={i}>{s}</span>)}</div>
      </div>

      <section className="section"><div className="wrap">
        <div className="section-head">
          <div><Ed k="servicesSection.eyebrow" as="p" className="eyebrow">{ss.eyebrow}</Ed><Ed k="servicesSection.title" as="h2">{ss.title}</Ed></div>
          <Ed k="servicesSection.intro" as="p">{ss.intro}</Ed>
        </div>
        <div className="grid three">{services.slice(0, 6).map((s) => <ServiceCard key={s.id} s={sv(s)} />)}</div>
        <div className="row spaced"><Link to="/services" className="text-link">View all services →</Link></div>
      </div></section>

      <section className="section soft-section"><div className="wrap">
        <div className="section-head"><div><p className="eyebrow">A simple place to start</p><h2>From "I have an idea"{"\n"}to "here's my next step."</h2></div></div>
        <div className="grid three steps">{(site.steps || []).map((st, i) => (
          <div key={i}><p className="step-no">{st.no}</p><h3>{st.title}</h3><p>{st.text}</p></div>
        ))}</div>
      </div></section>

      <section className="section"><div className="wrap grid two">
        <div>
          <Ed k="aboutSection.eyebrow" as="p" className="eyebrow">{ab.eyebrow}</Ed>
          <Ed k="aboutSection.title" as="h2" className="about-title">{ab.title}</Ed>
          <div className="about-label"><strong>{settings.ownerName}</strong><span>{ab.role}</span></div>
          <Portrait />
        </div>
        <div><p className="preline muted">{settings.about}</p><Link to="/book" className="text-link spaced">Let's have a conversation ↗</Link></div>
      </div></section>

      <section className="content-bottom"><div className="wrap"><div className="callout">
        <div><h2>What would you like to work on?</h2><p>Tell me what you have in mind. We can start from there.</p></div>
        <Link className="button light" to="/start">Start your request ↗</Link>
      </div></div></section>
    </>
  );
}

function Portrait() {
  const { site } = useSite();
  const url = mediaUrl((site.brand || {}).portraitUrl);
  return (
    <EdImg k="brand.portraitUrl" label="Michael's portrait" className={`portrait-panel${url ? "" : " empty"}`} role="img" aria-label={(site.brand || {}).portraitAlt || "Portrait of Michael Ephriam Khairi"}
      style={url ? { backgroundImage: `url(${url})` } : {}} data-testid="portrait-panel">
      {!url && <div className="ph">{isPreview() ? "Click to add Michael's photo" : "Michael's professional photo will appear here once provided."}</div>}
    </EdImg>
  );
}

const ServiceCard = ({ s }) => (
  <article className="card service-card"><span className="number">{s.number} /</span>
    <h3>{s.name}</h3><p>{s.short}</p>
    <Link to={`/services/${s.id}`} className="text-link">Explore service ↗</Link></article>
);

/* ------------------------------------------------------------------ services */
function Services() {
  const { catalog, site } = useSite();
  const { id } = useParams();
  const services = catalog.services || [];
  const ov = site.serviceOverrides || {};
  const sv = (s) => ({ ...s, ...(ov[s.id] || {}) });
  if (!id) return (<>
    <PageHead eyebrow="Services" title="Let's find your next step." lead="Select a service to see how I can help. You can request support in more than one area." />
    <div className="wrap content-bottom grid three">{services.map((s) => <ServiceCard key={s.id} s={sv(s)} />)}</div>
  </>);
  const s = sv(services.find((x) => x.id === id) || {});
  if (!s.id) return <NotFound />;
  return (<>
    <PageHead eyebrow={`Service ${s.number}`} title={s.name} lead={s.description} />
    <div className="wrap content-bottom grid two">
      <div className="card"><h3>What we can work on</h3><ul>{(s.deliverables || []).map((d, i) => <li key={i}>{d}</li>)}</ul>
        <p className="small muted">Your quote or package will define the deliverables, timeline, and price before paid work begins.</p>
        <Link className="button spaced" to={`/start?service=${s.id}`}>Request this service ↗</Link></div>
      <div className="card"><p className="eyebrow">Start with a conversation</p><h2 className="form-title">A plan built around your goals.</h2>
        <p className="muted small">Share what you want to accomplish, what you already have, and where you feel stuck.</p>
        <Link className="text-link" to="/book">View booking options →</Link></div>
    </div>
  </>);
}

/* ------------------------------------------------------------------ purchase (Packages page retired; /purchase kept for existing records) */
function Purchase() {
  const { packages, settings, me, reload } = useSite();
  const { id } = useParams();
  const nav = useNavigate();
  const [consent, setConsent] = useState(false);
  const p = packages.find((x) => x.id === id);
  if (!p) return <NotFound />;
  const renewal = p.kind === "subscription"
    ? `You will be charged ${money(p.amount)} every ${p.interval === "year" ? "year" : "month"} until you cancel. Cancel future renewals in your client portal before the next renewal date.`
    : `This is a one-time payment of ${money(p.amount)}.`;
  const checkout = async () => {
    try {
      const r = await apiPost("/checkout", { packageId: p.id, packageRevision: p.priceRevision, consent });
      window.location.assign(r.url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (<>
    <PageHead eyebrow="Review your package" title={p.name} lead={p.description} />
    <div className="wrap narrow content-bottom"><div className="card">
      <p className="price">{money(p.amount)}{p.kind === "subscription" && <span> / {p.interval}</span>}</p>
      <h3>Included support</h3><ul>{(p.includes || "").split("\n").filter(Boolean).map((i, k) => <li key={k}>{i}</li>)}</ul>
      <p className="info">{renewal}</p>
      <details className="spaced"><summary>Service and cancellation terms</summary>
        <p className="preline small spaced">{settings.terms}</p><p className="preline small spaced">{settings.cancellation}</p></details>
      {!settings.paymentsReady ? (
        <div className="info spaced">Online payments are being set up. <Link to="/start">Request a quote</Link> to discuss this package.</div>
      ) : me.role === "client" ? (
        <div className="stack spaced">
          <label className="check"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="purchase-consent" />
            <span>I agree to the included scope, service terms, cancellation policy, and {p.kind === "subscription" ? `recurring ${money(p.amount)} ${p.interval === "year" ? "yearly" : "monthly"} charge until canceled` : `one-time charge of ${money(p.amount)}`}.</span></label>
          <button className="button" disabled={!consent} onClick={checkout} data-testid="purchase-checkout-btn">Continue to secure payment ↗</button>
          <p className="small muted">Payment details are collected securely by Stripe.</p>
        </div>
      ) : (
        <button className="button spaced" onClick={() => { sessionStorage.setItem("mk_pending_package", p.id); nav("/login"); }} data-testid="purchase-login-btn">Sign in to continue ↗</button>
      )}
    </div></div>
  </>);
}

/* ------------------------------------------------------------------ book & contact */
function Book() {
  const { settings } = useSite();
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", stage: "", result: "", obstacle: "", timeline: "", availability: "", services: [], consent: false });
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setForm((f) => ({ ...f, services: f.services.includes(id) ? f.services.filter((x) => x !== id) : [...f.services, id] }));
  const { catalog } = useSite();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await apiPost("/leads", { ...form, kind: "consultation" });
      setDone({ ...r, name: form.name, email: form.email });
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  if (done) {
    let link = bookingLink(settings.calendarUrl, "consulting", { utm_content: done.id });
    if (link) { try { const u = new URL(link); u.searchParams.set("name", done.name); u.searchParams.set("email", done.email); link = u.href; } catch {} }
    return (<>
      <PageHead eyebrow="Free 15-minute consult" title={link ? "Choose your time." : "Your request is in."}
        lead={link ? "Your answers were saved. Select an available time below to finish booking." : "Your answers were saved. Michael will follow up by email to arrange a time."} />
      <div className="wrap content-bottom">
        <p className="info" data-testid="book-confirmation">Request {done.id}. This form alone does not book an appointment. {link ? "Your time is confirmed only after you finish the calendar and receive its confirmation." : "You do not need an account for this free consultation."}</p>
        {link ? (<>
          <iframe className="calendar-frame" title="Book a free 15-minute consultation" src={link} loading="lazy" data-testid="consult-calendar" />
          <p className="small spaced">Calendar not loading? <a href={link} target="_blank" rel="noopener noreferrer" data-testid="consult-calendar-newtab">Open the calendar in a new tab ↗</a></p>
        </>) : <Link to="/contact" className="text-link spaced">Send another question →</Link>}
      </div>
    </>);
  }
  return (<>
    <PageHead eyebrow="Free 15-minute consult" title="Let's start with your goals."
      lead={settings.calendarUrl ? "Tell me a little about your situation, then choose a time on the connected calendar. No account or payment is required." : "Tell me a little about your situation. Michael will follow up by email to arrange a time. No account or payment is required."} />
    <div className="wrap narrow content-bottom"><form className="card admin-form" onSubmit={submit} data-testid="book-form">
      <p className="form-note">This first call helps us see what support could fit. Paid work requires a separate quote or package agreement.</p>
      <div className="grid two">
        <Input label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="book-name" />
        <Input label="Email address *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="book-email" />
      </div>
      <Input label="Phone number (optional)" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
      <fieldset className="field"><legend>What would you like to discuss?</legend><div className="grid two">
        {(catalog.services || []).map((s) => (
          <label className="check" key={s.id}><input type="checkbox" checked={form.services.includes(s.id)} onChange={() => toggle(s.id)} /><span>{s.name}</span></label>
        ))}</div></fieldset>
      <div className="field"><label>Where are you now?</label>
        <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
          <option value="">Choose if applicable</option><option>Exploring an idea</option><option>Preparing to launch</option>
          <option>Already operating</option><option>Working on a personal budget</option><option>Something else</option></select></div>
      <TextArea label="What do you want help with, and what would a useful first step look like? *" value={form.result} onChange={(v) => setForm({ ...form, result: v })} required testid="book-result" />
      <TextArea label="What is your biggest obstacle right now? (optional)" value={form.obstacle} onChange={(v) => setForm({ ...form, obstacle: v })} />
      <Input label="Is there a deadline or preferred start date? (optional)" value={form.timeline} onChange={(v) => setForm({ ...form, timeline: v })} />
      <Input label="Preferred time zone or availability (optional)" value={form.availability} onChange={(v) => setForm({ ...form, availability: v })} placeholder="For example: Central time, weekday evenings" />
      <p className="small muted">Please do not include passwords, Social Security numbers, bank details, or card numbers.</p>
      <label className="check"><input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} required data-testid="book-consent" />
        <span>I agree that my answers may be used to respond and prepare for our conversation, as described in the <Link to="/privacy" target="_blank">privacy notice</Link>. I understand scheduling is handled by Calendly if connected.</span></label>
      <button className="button" disabled={busy} data-testid="book-submit">{settings.calendarUrl ? "Continue to calendar ↗" : "Send consultation request ↗"}</button>
    </form></div>
  </>);
}

function Contact() {
  const { settings } = useSite();
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", result: "", consent: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await apiPost("/leads", { ...form, kind: "message" }); setDone(r); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  if (done) return (<>
    <PageHead eyebrow="Contact" title="Your message is in." lead="Michael will review what you shared." />
    <div className="wrap narrow content-bottom"><div className="card">
      <p>Keep this reference number: <strong data-testid="contact-reference">{done.id}</strong>.</p>
      <p className="small spaced">{done.confirmationEmail ? "A confirmation email was sent." : "An email confirmation is unavailable; your request is saved in the private dashboard."}</p>
      <Link to="/book" className="button spaced">Request a free 15-minute consult ↗</Link></div></div>
  </>);
  return (<>
    <PageHead eyebrow="Contact Michael" title="Send a question or request." lead="Share what you are working on. You can ask a question without creating an account." />
    <div className="wrap narrow content-bottom"><form className="card admin-form" onSubmit={submit} data-testid="contact-form">
      <div className="grid two">
        <Input label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
        <Input label="Email address *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
      </div>
      <Input label="What is your request about?" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
      <TextArea label="Your message *" value={form.result} onChange={(v) => setForm({ ...form, result: v })} required testid="contact-message" />
      <p className="small muted">Please leave out passwords, Social Security numbers, bank details, and card numbers.</p>
      <label className="check"><input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} required data-testid="contact-consent" />
        <span>I agree to the <Link to="/privacy" target="_blank">privacy notice</Link> for responding to my request.</span></label>
      <button className="button" disabled={busy} data-testid="contact-submit">Send my request ↗</button>
    </form>
    </div>
  </>);
}

/* ------------------------------------------------------------------ intake wizard */
function Start() {
  const { catalog, reload } = useSite();
  const nav = useNavigate();
  const services = catalog.services || [], common = catalog.commonQuestions || [];
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(null);
  const sp = new URLSearchParams(useLocation().search);
  const [w, setW] = useState({ services: sp.get("service") && services.some((s) => s.id === sp.get("service")) ? [sp.get("service")] : [], name: "", email: "", phone: "", city: "", contact: "Email", common: {}, answers: {} });
  const selected = services.filter((s) => w.services.includes(s.id));
  const titles = ["Where could you use support?", "Tell me about your goals.", "Let's get into the details.", "Review your request."];
  const toggle = (id) => setW((x) => ({ ...x, services: x.services.includes(id) ? x.services.filter((y) => y !== id) : [...x.services, id] }));
  const setCommon = (id, v) => setW((x) => ({ ...x, common: { ...x.common, [id]: v } }));
  const setAns = (sid, id, v) => setW((x) => ({ ...x, answers: { ...x.answers, [sid]: { ...(x.answers[sid] || {}), [id]: v } } }));
  const next = async () => {
    if (step === 0 && !w.services.length) return toast.error("Choose at least one service.");
    if (step < 3) return setStep(step + 1);
    try { const r = await apiPost("/requests", { ...w, consent: true }); setSubmitted(r); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (submitted) return (<>
    <PageHead eyebrow="Request received" title="You've taken the first step." lead="Your request has been saved for review." />
    <div className="wrap narrow content-bottom"><div className="card">
      <h3>What happens next?</h3><p className="spaced">I'll review your goals and the details you shared. Keep your reference number for any follow-up.</p>
      <p className="small muted spaced" data-testid="intake-reference">Reference: {submitted.id}</p>
      <p className="small spaced">{submitted.confirmationEmail ? "A confirmation email has also been sent." : "Please keep this confirmation; an email confirmation is not available right now."}</p>
      <div className="row spaced"><Link to="/book" className="button">Book a conversation ↗</Link><Link to="/login" className="button secondary">Client portal</Link></div>
    </div></div>
  </>);
  return (<>
    <PageHead eyebrow="Start a request" title="Tell me what you have in mind." lead="A few thoughtful questions will help us make our first conversation useful." />
    <div className="wrap content-bottom"><div className="wizard">
      <p className="eyebrow">Step {step + 1} of 4</p>
      <div className="progress">{[0, 1, 2, 3].map((i) => <span key={i} className={i <= step ? "done" : ""} />)}</div>
      <div className="card form-card">
        <h2 className="form-title">{titles[step]}</h2>
        {step === 0 && <><p className="form-note">Choose all that apply. The next questions will match your selections.</p>
          <div className="grid two">{services.map((s) => (
            <label className="check-card" key={s.id}><input type="checkbox" checked={w.services.includes(s.id)} onChange={() => toggle(s.id)} data-testid={`intake-service-${s.id}`} />
              <span><strong>{s.name}</strong><span className="sub">{s.short}</span></span></label>))}</div></>}
        {step === 1 && <><p className="form-note">Fields marked * are required. Approximate answers are welcome.</p>
          <div className="fields"><div className="grid two">
            <Input label="Your name *" value={w.name} onChange={(v) => setW({ ...w, name: v })} testid="intake-name" />
            <Input label="Email address *" type="email" value={w.email} onChange={(v) => setW({ ...w, email: v })} testid="intake-email" />
            <Input label="Phone number (optional)" value={w.phone} onChange={(v) => setW({ ...w, phone: v })} />
            <Input label="City and state (optional)" value={w.city} onChange={(v) => setW({ ...w, city: v })} />
          </div>
            <div className="field"><label>Preferred contact method</label>
              <select value={w.contact} onChange={(e) => setW({ ...w, contact: e.target.value })}><option>Email</option><option>Phone</option><option>Text</option></select></div>
            {common.map((f) => <Field key={f.id} f={f} value={w.common[f.id]} onChange={(v) => setCommon(f.id, v)} />)}
          </div></>}
        {step === 2 && <><p className="form-note">Answer what you can. Please leave out passwords, SSNs, banking details, and card numbers.</p>
          <div className="stack">{selected.map((s) => (
            <section className="stack" key={s.id}><h3>{s.name}</h3><div className="fields">
              {s.questions.map((f) => <Field key={f.id} f={f} value={(w.answers[s.id] || {})[f.id]} onChange={(v) => setAns(s.id, f.id, v)} />)}</div></section>))}</div></>}
        {step === 3 && <><p className="form-note">Make sure these details reflect what you need. You can go back to make changes.</p>
          <div className="review"><dl>
            <dt>Name</dt><dd>{w.name}</dd><dt>Email</dt><dd>{w.email}</dd>
            <dt>Services</dt><dd>{selected.map((s) => s.name).join(", ")}</dd>
            {common.filter((f) => w.common[f.id] && w.common[f.id].length).map((f) => <React.Fragment key={f.id}><dt>{f.label}</dt><dd>{Array.isArray(w.common[f.id]) ? w.common[f.id].join(", ") : w.common[f.id]}</dd></React.Fragment>)}
          </dl></div>
          <div className="info spaced">Submitting this form requests a review of your needs. It does not begin paid work or reserve an appointment.</div></>}
        <div className="form-actions">
          {step ? <button type="button" className="button secondary" onClick={() => setStep(step - 1)} data-testid="intake-back">Back</button> : <span />}
          <button type="button" className="button" onClick={next} data-testid="intake-next">{step === 3 ? "Send my request ↗" : "Continue →"}</button>
        </div>
      </div>
    </div></div>
  </>);
}

/* ------------------------------------------------------------------ login / portal */
function Login() {
  const { me, reload } = useSite();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token");
  useEffect(() => {
    if (token) (async () => {
      try {
        await apiPost("/auth/verify", { token });
        await reload();
        const pending = sessionStorage.getItem("mk_pending_package");
        sessionStorage.removeItem("mk_pending_package");
        nav(pending ? `/purchase/${pending}` : "/portal");
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []); // eslint-disable-line
  if (me.role === "client") return (<>
    <PageHead eyebrow="Welcome back" title="Your portal is ready." />
    <div className="wrap content-bottom"><Link to="/portal" className="button">Open my portal →</Link></div></>);
  const link = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await apiPost("/auth/link", { email }); setSent(r.message); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (<>
    <PageHead eyebrow="Your client portal" title="A place to keep things together." lead="View your requests, review quotes, pay invoices, and manage subscriptions." />
    <div className="wrap"><div className="card auth-box">
      <h2>{token ? "Signing you in…" : "Sign in with your email"}</h2>
      {!token && (sent ? <p className="info spaced" data-testid="login-link-sent">{sent}</p> : (
        <form className="fields" onSubmit={link}>
          <div className="field"><label>Email address</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" /></div>
          <p className="small muted">We'll email you a private link that expires in 15 minutes. Use the same email you used for your request or purchase.</p>
          <button className="button" disabled={busy} data-testid="login-submit">Email my sign-in link →</button>
        </form>))}
      <p className="small spaced muted">New here? You can sign in before making a purchase or after sending a request.</p>
    </div></div>
  </>);
}

function Portal() {
  const { me, reload, catalog } = useSite();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(async () => { try { setData(await apiGet("/portal")); } catch (e) { toast.error(apiErr(e)); } }, []);
  useEffect(() => { if (me.role === "client") load(); }, [me.role, load]);
  if (me.role !== "client") return <Login />;
  if (!data) return <div className="loading">Loading your portal…</div>;
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); await load(); } catch (e) { toast.error(apiErr(e)); } };
  const svName = (id) => (catalog.services || []).find((s) => s.id === id)?.name || id;
  return (<>
    <PageHead eyebrow="Your client portal" title="Your next steps, in one place." lead={me.email} />
    <div className="wrap content-bottom">
      <div className="row between"><Link to="/start" className="button secondary small">New request</Link>
        <button className="text-button" onClick={async () => { await apiPost("/auth/logout"); await reload(); nav("/"); }} data-testid="portal-logout">Sign out</button></div>
      <div className="portal-grid spaced">
        <section><h2>My requests</h2>{data.requests.length ? data.requests.map((r) => (
          <article className="card portal-card" key={r.id}><div className="row between"><h3>{r.services.map(svName).join(" · ")}</h3><Status s={r.status} /></div>
            <p className="spaced">{(r.common || {}).result}</p><p className="small muted">Submitted {dateFmt(r.created)}</p></article>
        )) : <Empty>Your submitted requests will appear here.</Empty>}</section>

        <div className="grid two">
          <section><h2>Quotes</h2>{data.quotes.length ? data.quotes.map((q) => (
            <article className="card portal-card" key={q.id}><div className="row between"><h3>{q.title}</h3><Status s={q.status} /></div>
              <p className="preline spaced">{q.scope}</p><ul>{q.items.map((i, k) => <li key={k}>{i.description}: {money(i.amount)}</li>)}</ul>
              <strong>{money(q.amount)}</strong><p className="small muted">Valid through {dateFmt(q.validUntil + "T23:59:59Z")} (UTC)</p>
              {q.status === "sent" && (
                <div className="row"><button className="button small" onClick={() => act(() => apiPost(`/quotes/${q.id}`, { action: "accept", consent: true }), "Quote accepted.")} data-testid={`accept-quote-${q.id}`}>Accept quote</button>
                  <button className="button secondary small" onClick={() => { if (window.confirm("Decline this quote?")) act(() => apiPost(`/quotes/${q.id}`, { action: "decline" }), "Quote declined."); }}>Decline</button></div>)}
              {q.status === "accepted" && <a className="text-link spaced" href={`${process.env.REACT_APP_BACKEND_URL}/api/agreements/${q.id}`} target="_blank" rel="noopener noreferrer" data-testid={`download-agreement-${q.id}`}>Download signed agreement (PDF) ↗</a>}
            </article>)) : <Empty>Your quotes will appear here when ready.</Empty>}</section>

          <section><h2>Invoices & receipts</h2>{data.invoices.length ? data.invoices.map((i) => (
            <article className="card portal-card" key={i.id}><div className="row between"><h3>{i.description}</h3><Status s={i.status} /></div>
              <p className="spaced">{money(i.amount, i.currency)} {i.number ? `· ${i.number}` : ""}</p>
              {i.refunded ? <p className="small muted">Refunded: {money(i.refunded, i.currency)}</p> : null}
              <div className="row">{i.hostedUrl && <a href={safeUrl(i.hostedUrl)} target="_blank" rel="noopener noreferrer" className="button small">{i.status === "paid" ? "View receipt" : "View invoice / pay"} ↗</a>}
                {i.pdfUrl && <a href={safeUrl(i.pdfUrl)} target="_blank" rel="noopener noreferrer" className="text-link">Download PDF</a>}</div></article>
          )) : <Empty>You do not have any invoices yet.</Empty>}</section>
        </div>

        <section><h2>Packages & subscriptions</h2>
          {data.subscriptions.map((s) => (
            <article className="card portal-card" key={s.id}><div className="row between"><h3>{s.name}</h3><Status s={s.status} /></div>
              <p className="spaced">{money(s.amount)} / {s.interval || "billing period"}</p>
              <p className="small muted">{s.cancelAtPeriodEnd ? "Renewal canceled. Current period ends " : "Current billing period ends "}{dateFmt(s.currentPeriodEnd)}</p>
              <div className="row"><button className="button secondary small" onClick={async () => { try { const r = await apiPost("/billing-portal"); window.location.assign(r.url); } catch (e) { toast.error(apiErr(e)); } }}>Manage billing ↗</button>
                {["active", "trialing", "past_due"].includes(s.status) && !s.cancelAtPeriodEnd && <button className="text-button" onClick={() => { if (window.confirm("Cancel future renewals? Your current paid period will continue.")) act(() => apiPost(`/subscriptions/${s.id}`, { action: "cancel", confirm: true }), "Future renewals canceled."); }}>Cancel future renewals</button>}
                {s.bookingUrl && <a href={safeUrl(s.bookingUrl)} target="_blank" rel="noopener noreferrer" className="text-link">Schedule included support ↗</a>}</div></article>))}
          {data.orders.map((o) => (
            <article className="card portal-card" key={o.id}><div className="row between"><h3>{o.name}</h3><Status s={o.status} /></div><p>{money(o.amount)}</p>
              {o.bookingUrl && <a className="text-link" href={safeUrl(o.bookingUrl)} target="_blank" rel="noopener noreferrer">Schedule your session ↗</a>}</article>))}
          {!data.orders.length && !data.subscriptions.length && <Empty>Your package purchases and ongoing support will appear here.</Empty>}</section>

        <section><h2>Projects</h2>{(data.projects || []).length ? data.projects.map((p) => (
          <article className="card portal-card" key={p.id}><div className="row between"><h3>{p.title}</h3><Status s={p.status} /></div>
            <p className="preline spaced">{p.scope}</p>
            <p className="small muted">Agreed {money(p.amount)} · Accepted {dateFmt(p.acceptedAt)} · Agreement version {p.agreementVersion}</p>
            {(p.deadlines || []).length ? <><h4 className="spaced">Deadlines</h4><ul>{p.deadlines.map((d, k) => <li key={k}>{d.label}{d.date ? ` (${dateFmt(d.date)})` : ""}{d.done ? " ✓" : ""}</li>)}</ul></> : null}
            {(p.documents || []).length ? <div className="row">{p.documents.map((d, k) => <a key={k} className="text-link" href={d.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${d.url}` : safeUrl(d.url)} target="_blank" rel="noopener noreferrer">{d.label} ↗</a>)}</div> : null}
            {(p.approvals || []).filter((a) => a.status === "pending").map((a) => (
              <div className="info spaced" key={a.id}>{a.label}<div className="row spaced"><button className="button small" onClick={() => act(() => apiPost(`/projects/${p.id}/approve`, { approvalId: a.id, consent: true }), "Approved.")} data-testid={`approve-${a.id}`}>Approve</button></div></div>))}
            {(p.approvals || []).filter((a) => a.status === "approved").map((a) => (<p className="small muted" key={a.id}>✓ Approved: {a.label} ({dateFmt(a.approvedAt)})</p>))}
          </article>)) : <Empty>Your project workspace appears here after you accept a quote.</Empty>}</section>

        <section><h2>Appointments</h2>{data.bookings.length ? data.bookings.map((b) => (
          <article className="card portal-card" key={b.id}><div className="row between"><h3>{b.title}</h3><Status s={b.status} /></div>
            <p className="spaced">{timeFmt(b.startsAt)}</p><p className="tiny muted">Shown in your browser's local time zone.</p></article>
        )) : <Empty>Calendar confirmations arrive by email. Appointments also appear here when calendar synchronization is enabled.</Empty>}</section>
      </div>
    </div>
  </>);
}

function Policy({ type }) {
  const { settings } = useSite();
  const labels = { privacy: "Privacy notice", terms: "Service terms", cancellation: "Cancellation & refund policy" };
  return (<>
    <PageHead eyebrow="Working together" title={labels[type]} />
    <div className="wrap narrow content-bottom">
      {!settings.policiesReady && <div className="banner" style={{ marginBottom: 18 }} data-testid="policy-draft-banner">Draft under owner/legal review. These terms are not yet in effect, and paid checkout stays off until Michael approves the final wording.</div>}
      <div className="card policy" data-testid={`policy-${type}`}>{settings[type]}</div>
      {settings.email && <p className="small spaced">Questions? <a href={`mailto:${settings.email}`}>Contact {settings.name}.</a></p>}</div>
  </>);
}

function NotFound() {
  return (<><PageHead eyebrow="Page not found" title="Let's get you back on track." />
    <div className="wrap content-bottom"><Link to="/" className="button">Back to home →</Link></div></>);
}

/* ------------------------------------------------------------------ shared inputs */
export const Input = ({ label, value, onChange, type = "text", required, placeholder, testid }) => (
  <div className="field"><label>{label}</label><input type={type} value={value || ""} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} /></div>
);
export const TextArea = ({ label, value, onChange, required, testid }) => (
  <div className="field"><label>{label}</label><textarea value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} /></div>
);
export function Field({ f, value, onChange }) {
  const label = <>{f.label}{f.required && <span aria-hidden> *</span>}</>;
  if (f.type === "checks") return (
    <fieldset className="field"><legend>{label}</legend><div className="checks">
      {f.options.map((o) => (<label className="check" key={o}><input type="checkbox" checked={(value || []).includes(o)}
        onChange={() => onChange((value || []).includes(o) ? (value || []).filter((x) => x !== o) : [...(value || []), o])} /><span>{o}</span></label>))}
    </div></fieldset>);
  if (f.type === "select") return (
    <div className="field"><label>{label}</label><select value={value || ""} onChange={(e) => onChange(e.target.value)} required={f.required}>
      <option value="">Choose an option</option>{f.options.map((o) => <option key={o}>{o}</option>)}</select></div>);
  if (f.type === "textarea") return <TextArea label={label} value={value} onChange={onChange} required={f.required} />;
  return <Input label={label} type={f.type === "url" ? "url" : f.type === "email" ? "email" : "text"} value={value} onChange={onChange} required={f.required} />;
}
export const Status = ({ s }) => (
  <span className={`pill ${["paid", "accepted", "active", "completed"].includes(s) ? "dark" : ["new", "draft", "past_due"].includes(s) ? "warn" : ""}`}>{String(s || "").replace(/_/g, " ")}</span>
);
export const Empty = ({ children }) => <div className="empty">{children}</div>;

/* ------------------------------------------------------------------ personal home + insurance + online */
function PersonalHome() {
  const { site, settings } = useSite();
  const h = site.home3 || { cards: [] };
  const nums = { consulting: "01", insurance: "02", online: "03", about: "04" };
  return (<>
    <div className="wrap"><section className="section" style={{ paddingBottom: 30 }}>
      <p className="eyebrow">{h.eyebrow}</p>
      <h1 style={{ whiteSpace: "pre-line", maxWidth: 780 }} data-testid="personal-home-title">{h.title}</h1>
      <p className="lead">{h.lead}</p>
    </section></div>
    <div className="wrap content-bottom">
      <div className={`grid ${(h.cards || []).length === 4 ? "four" : "three"}`}>{(h.cards || []).map((c) => (
        <Link to={c.url} key={c.key} className="card service-card" style={{ textDecoration: "none" }} data-testid={`home-card-${c.key}`}>
          <span className="number">{nums[c.key] || "•"} /</span><h3>{c.title}</h3><p>{c.text}</p>
          <span className="text-link" style={{ marginTop: "auto" }}>{c.cta} ↗</span></Link>))}
      </div>
      <section className="section grid two" style={{ marginTop: 10 }}>
        <div><p className="eyebrow">About</p><h2 className="about-title">{settings.ownerName}</h2><Portrait /></div>
        <div><p className="preline muted">{settings.about}</p>
          <Link to="/consulting" className="text-link spaced">See how I work ↗</Link></div>
      </section>
    </div>
  </>);
}

function LifeInsurance() {
  const { insurance, settings } = useSite();
  const ins = insurance || {};
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", question: "", preferredTime: "", consent: false });
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { setDone(await apiPost("/insurance-inquiry", f)); } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); } };
  return (<>
    <PageHead eyebrow="Life Insurance" title="Ask me about life insurance." lead={ins.intro} />
    <div className="wrap narrow content-bottom">
      <div className="info" data-testid="insurance-disclaimer">{ins.disclaimer}</div>
      <p className="small muted spaced">{ins.name} · National Producer Number (NPN): <strong data-testid="insurance-npn">{ins.npn}</strong></p>
      <div className="row spaced"><Link className="button" to="/life-insurance/book" data-testid="insurance-schedule-link">{settings.insuranceCalendarUrl ? "Schedule a free conversation ↗" : "Ask about scheduling ↗"}</Link>
        {ins.bookingUrl ? <a className="button secondary small" href={safeUrl(ins.bookingUrl)} target="_blank" rel="noopener noreferrer">External referral ↗</a> : null}</div>
      <div id="ask" />
      {done ? <div className="card spaced"><h3>Thank you.</h3><p className="spaced">Reference <strong data-testid="insurance-ref">{done.id}</strong>. This is a general question, not an application, offer, or policy. I'll follow up personally.</p></div>
        : <form className="card admin-form spaced" onSubmit={submit} data-testid="insurance-form">
          <p className="form-note">A general question only. Please do not include health history, SSN, policy/account numbers, or payment details.</p>
          <div className="grid two"><Input label="Full name *" value={f.name} onChange={(v) => setF({ ...f, name: v })} required testid="ins-name" />
            <Input label="Email address *" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} required testid="ins-email" /></div>
          <Input label="Phone (optional)" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} />
          <TextArea label="Your general question *" value={f.question} onChange={(v) => setF({ ...f, question: v })} required testid="ins-question" />
          <Input label="Preferred contact time (optional)" value={f.preferredTime} onChange={(v) => setF({ ...f, preferredTime: v })} />
          <label className="check"><input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} required data-testid="ins-consent" />
            <span>I agree to the <Link to="/privacy" target="_blank">privacy notice</Link>. I understand this is a general question, not an application or policy, and it will not be turned into a consulting quote or invoice.</span></label>
          <button className="button" disabled={busy} data-testid="ins-submit">Send my question ↗</button>
        </form>}
    </div>
  </>);
}

function SocialHeader() {
  const { site } = useSite();
  const SOCIAL_NAV = useMenus().social.items;
  const brand = site.brand || {};
  const [open, setOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); setExploreOpen(false); }, [loc.pathname, loc.hash]);
  return (
    <header className="soc-header">
      <div className="soc-header-in">
        <div className="soc-header-left">
          <div className="explore-wrap">
            <button className="soc-explore" onClick={() => setExploreOpen((o) => !o)} aria-expanded={exploreOpen} data-testid="explore-menu-btn"><MenuIcon size={15} /> Explore</button>
            {exploreOpen && <div className="explore-pop soc-pop" data-testid="explore-menu">
              {Object.entries(AREA_MENUS).map(([k, m]) => <Link key={k} to={m.items[0][1]} className={k === "social" ? "active" : ""} data-testid={`explore-${k}`}>{m.label}</Link>)}
            </div>}
          </div>
          <Link className="soc-brand" to="/" aria-label="Michael Ephriam Khairi, home" data-testid="brand-home-link">
            <img src={mediaUrl(brand.logoUrl) || "/mk-logo.png"} alt={brand.logoAlt || "MK monogram logo"} /><span>MK</span>
          </Link>
        </div>
        <button className="soc-menu-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="menu-toggle">Menu</button>
        <nav className={`soc-nav${open ? " open" : ""}`} aria-label="Social navigation">
          {SOCIAL_NAV.map(([label, url]) => (
            <Link key={url} to={url} className={(loc.pathname + loc.hash) === url ? "active" : ""} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}>{label}</Link>))}
        </nav>
      </div>
    </header>
  );
}

function SocialFooter() {
  const { site } = useSite();
  const brand = site.brand || {};
  const o = site.online || {};
  return (
    <footer className="soc-footer">
      <div className="soc-footer-in">
        <Link className="soc-footer-brand" to="/"><img src={mediaUrl(brand.logoUrl) || "/mk-logo.png"} alt="MK" />
          <div><strong>Michael Ephriam Khairi</strong><span>{o.eyebrow || "The Creator Hub"}</span></div></Link>
        <div className="soc-footer-links">
          <Link to="/online#posts">Posts</Link><Link to="/online#videos">Videos</Link><Link to="/online#membership">Membership</Link>
          <Link to="/online#support">Support</Link><Link to="/online#links">Social links</Link>
        </div>
        <div className="soc-footer-switch">
          <Link to="/consulting">Consulting</Link><Link to="/life-insurance">Life insurance</Link><Link to="/about">About me</Link>
        </div>
      </div>
      <div className="soc-footer-bottom"><span>© {new Date().getFullYear()} Michael Ephriam Khairi</span><Link to="/admin">Owner login</Link></div>
    </footer>
  );
}

const SocialPostCard = ({ p }) => (
  <Link to={`/online/${p.slug}`} className="soc-post" data-testid={`post-${p.slug}`}>
    <div className="soc-post-media">
      {p.coverUrl ? <img src={mediaUrl(p.coverUrl)} alt="" loading="lazy" /> : <div className="soc-post-ph" />}
      <div className="soc-badges">{p.visibility === "member" && <span className="soc-badge gold">Members</span>}{p.hasVideo && <span className="soc-badge">▶ Video</span>}</div>
    </div>
    <div className="soc-post-body">
      <h3 className="soc-post-title">{p.title}</h3>
      <p className="soc-post-ex">{p.excerpt}</p>
      <span className="soc-post-cta">{p.locked ? "Members only" : p.hasVideo ? "Watch" : "Read"} →</span>
    </div>
  </Link>
);

function Online() {
  const { site, creator, me } = useSite();
  const o = site.online || { socials: [] };
  const c = creator || {};
  const [data, setData] = useState(null);
  const sp = new URLSearchParams(useLocation().search);
  useEffect(() => { apiGet("/posts").then(setData).catch(() => setData({ posts: [], member: false })); }, []);
  const join = async () => { try { const r = await apiPost("/creator/membership/checkout"); window.location.assign(r.url); } catch (e) { toast.error(apiErr(e)); } };
  const support = async (amt) => { const email = me.email || window.prompt("Your email for the receipt:"); if (!email) return; try { const r = await apiPost("/creator/support", { amount: amt, email }); window.location.assign(r.url); } catch (e) { toast.error(apiErr(e)); } };
  const posts = data?.posts || [];
  const videoPosts = posts.filter((p) => p.hasVideo);
  const gallery = o.gallery || [];
  const heroImg = o.heroImage;
  return (
    <div className="soc">
      <section className="soc-hero">
        <div className="soc-hero-copy">
          <span className="soc-eyebrow">{o.eyebrow || "The Creator Hub"}</span>
          <h1 className="soc-display" data-testid="social-title">{o.title}</h1>
          <p className="soc-lead">{o.intro}</p>
          <div className="soc-hero-actions">
            <a href="#posts" className="soc-btn" data-testid="social-explore-feed">Explore the feed</a>
            <a href="#links" className="soc-btn ghost">Find me online</a>
          </div>
        </div>
        <div className="soc-hero-media">
          <EdImg k="online.heroImage" label="Social hero photo" className="soc-hero-media-in">{heroImg ? <img src={mediaUrl(heroImg) || heroImg} alt={o.heroImageAlt || "Michael Ephriam Khairi"} /> : <div className="soc-media-ph">{isPreview() ? "Click to add a photo" : "Photo coming soon"}</div>}</EdImg>
        </div>
      </section>

      {sp.get("member") === "welcome" && <div className="soc-note"><span>Welcome! Your membership is being activated. Refresh in a moment to see member posts.</span></div>}
      {sp.get("support") === "thanks" && <div className="soc-note"><span>Thank you for supporting my work.</span></div>}

      {gallery.length ? <section className="soc-section" id="gallery">
        <div className="soc-head"><span className="soc-eyebrow">Moments</span><h2 className="soc-h2">In frame.</h2></div>
        <div className="soc-gallery" data-testid="social-gallery">{gallery.map((g, i) => { const u = typeof g === "string" ? g : g.url; return <EdImg k={`online.gallery.${i}`} label={`Moments photo ${i + 1}`} as="figure" className="soc-shot" key={i}><img src={mediaUrl(u) || u} alt={(g && g.alt) || ""} loading="lazy" /></EdImg>; })}</div>
      </section> : null}

      <div className="soc-divider" />
      <section className="soc-section" id="posts">
        <div className="soc-head"><span className="soc-eyebrow">Latest</span><h2 className="soc-h2">Posts &amp; stories.</h2></div>
        <div className="soc-feed" data-testid="social-feed">
          {posts.map((p) => <SocialPostCard key={p.id} p={p} />)}
          {!posts.length && <div className="soc-empty">New posts, photos, and videos will land here.</div>}
        </div>
      </section>

      <section className="soc-section" id="videos">
        <div className="soc-head"><span className="soc-eyebrow">Watch</span><h2 className="soc-h2">Videos.</h2></div>
        <div className="soc-videos" data-testid="social-videos">
          {videoPosts.map((p) => <SocialPostCard key={p.id} p={p} />)}
          {!videoPosts.length && <div className="soc-empty">Video drops are coming soon.</div>}
        </div>
      </section>

      <div className="soc-divider" />
      <section className="soc-section" id="membership">
        {c.membership ? <div className="soc-tier" data-testid="social-membership">
          <span className="soc-eyebrow">Membership</span><h2 className="soc-h2">{c.membership.name}</h2>
          <p className="soc-price">{money(c.membership.monthly)}<span style={{ fontSize: 18, color: "#a1a1aa" }}> / month</span></p>
          <p className="soc-lead">{c.membership.benefits || "Member-only posts and videos."}</p>
          <div className="soc-hero-actions">{me.role === "client" ? <button className="soc-btn" onClick={join} data-testid="join-membership">Become a member →</button> : <Link className="soc-btn" to="/login">Sign in to join</Link>}</div>
        </div> : <div className="soc-tier locked" data-testid="social-membership">
          <span className="soc-eyebrow">Membership</span><h2 className="soc-h2">{o.membershipTagline || "Membership opens soon."}</h2>
          <p className="soc-lead">An optional members tier with behind-the-scenes posts and videos is on the way. Check back soon.</p></div>}
      </section>

      <section className="soc-section" id="support">
        {c.support?.enabled ? <div className="soc-support" data-testid="social-support">
          <span className="soc-eyebrow">Support</span><h2 className="soc-h2">{c.support.label || "Support my work"}</h2>
          <p className="soc-lead">A one-time thank-you. Personal creator support, not a tax-deductible donation.</p>
          <div className="soc-support-amts">{(c.support.amounts || []).map((a) => <button key={a} className="soc-btn ghost" onClick={() => support(a)} data-testid={`support-${a}`}>{money(a)}</button>)}</div>
        </div> : <div className="soc-support locked" data-testid="social-support">
          <span className="soc-eyebrow">Support</span><h2 className="soc-h2">{o.supportTagline || "Support coming soon."}</h2>
          <p className="soc-lead">A simple way to support the work will appear here once it's switched on.</p></div>}
      </section>

      <div className="soc-divider" />
      <section className="soc-section" id="links">
        <div className="soc-head"><span className="soc-eyebrow">Elsewhere</span><h2 className="soc-h2">Find me online.</h2></div>
        {(o.socials || []).length ? <div className="soc-links" data-testid="social-links">
          {o.socials.map((s, i) => <SmartLink key={i} className="soc-link" to={s.url} data-testid={`social-${i}`}><span>{s.label}</span><ArrowUpRight size={18} /></SmartLink>)}
        </div> : <div className="soc-empty">Verified links will appear here once Michael adds them.</div>}
      </section>
    </div>
  );
}

function SocialSlug() {
  const { site } = useSite();
  const { slug } = useParams();
  return findCustomPage(site, "social", slug) ? <CustomPage section="social" /> : <PostDetail />;
}

function PostDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { apiGet(`/posts/${slug}`).then(setD).catch((e) => setErr(apiErr(e))); }, [slug]);
  if (err) return <NotFound />;
  if (!d) return <div className="loading">Loading…</div>;
  if (d.locked) return (<div className="soc"><div className="soc-detail">
    <span className="soc-eyebrow">Members only</span><h1 className="soc-display">{d.post.title}</h1><p className="soc-lead">{d.post.excerpt}</p>
    <div className="soc-empty" style={{ marginTop: 24 }}>This content is for members. <Link to="/online#membership" style={{ color: "#d4af37" }}>Become a member</Link> to read and watch.</div>
    <Link className="soc-back" to="/online">← Back to the feed</Link></div></div>);
  const p = d.post;
  return (<div className="soc"><div className="soc-detail">
    <span className="soc-eyebrow">From Michael</span><h1 className="soc-display">{p.title}</h1><p className="soc-lead">{p.excerpt}</p>
    {p.coverUrl && <img className="soc-detail-media" src={mediaUrl(p.coverUrl)} alt="" />}
    {d.videoUrl && <video className="soc-detail-media" controls controlsList="nodownload" src={mediaUrl(d.videoUrl)} data-testid="post-video" />}
    <div className="soc-body">{p.body}</div>
    {p.caption && <p className="soc-post-ex" style={{ marginTop: 18 }}>{p.caption}</p>}
    {p.transcript && <details className="spaced" style={{ marginTop: 18, color: "#a1a1aa" }}><summary>Transcript</summary><p className="soc-body" style={{ fontSize: 14, marginTop: 10 }}>{p.transcript}</p></details>}
    <Link className="soc-back" to="/online">← Back to the feed</Link>
  </div></div>);
}

function InsuranceSchedule() {
  const { settings, insurance } = useSite();
  const link = bookingLink(settings.insuranceCalendarUrl, "insurance");
  return (<>
    <PageHead eyebrow="Life insurance" title="Schedule a free conversation." lead={link ? "Pick a time that works for you. This is a general life-insurance conversation, not an application, offer, or policy." : "Scheduling is being set up. In the meantime, send a question and I'll follow up personally."} />
    <div className="wrap content-bottom">
      <p className="info">{(insurance || {}).name} · NPN {(insurance || {}).npn}. A booking is a conversation only, not an offer, application, or coverage. Your life-insurance conversation is kept separate from any consulting request.</p>
      {link ? <>
        <iframe className="calendar-frame" title="Schedule a life-insurance conversation" src={link} loading="lazy" data-testid="insurance-calendar" />
        <p className="small spaced">Calendar not loading? <a href={link} target="_blank" rel="noopener noreferrer" data-testid="insurance-calendar-newtab">Open it in a new tab ↗</a></p>
      </> : <div className="empty" data-testid="insurance-schedule-pending"><h3>Scheduling is being set up.</h3><p className="spaced">In the meantime, <Link to="/life-insurance#ask">ask a question</Link> and I'll follow up.</p></div>}
    </div>
  </>);
}

/* ------------------------------------------------------------------ insurance options (neutral + reviewed cards) */
function InsuranceOptions() {
  const { insurance } = useSite();
  const ins = insurance || {};
  const [d, setD] = useState(null);
  useEffect(() => { apiGet("/insurance/options").then(setD).catch(() => setD({ cards: [], naicLinks: [], pendingReview: true, intro: "" })); }, []);
  return (<>
    <PageHead eyebrow="Life insurance" title="Coverage options: a neutral overview" lead={d?.intro} />
    <div className="wrap content-bottom">
      <p className="small muted spaced">{ins.name} · National Producer Number (NPN): {ins.npn}</p>
      {d && (d.cards || []).length ? (
        <div className="options-list spaced" data-testid="options-cards">
          {d.cards.map((c) => (
            <article className="card option-card" key={c.key} data-testid={`option-${c.key}`}>
              <h3>{c.name}</h3>
              <h4>What it is</h4><p className="preline">{c.whatIs}</p>
              <h4>When someone might compare it</h4><p className="preline">{c.whenConsider}</p>
              <h4>Why it may be considered</h4><p className="preline">{c.whyConsider}</p>
              <h4>Limitations &amp; costs</h4><p className="preline">{c.limitations}</p>
              <h4>Useful questions to ask</h4><p className="preline">{c.questions}</p>
            </article>))}
        </div>
      ) : (
        <div className="info spaced" data-testid="options-pending">Detailed, product-specific explanations of term life, whole life, indexed universal life (IUL), and annuities are prepared but kept private until the exact wording, disclosures, and my current product authority are reviewed and approved. Please use the trusted, independent consumer guides below in the meantime.</div>
      )}
      <h3 className="spaced">Independent consumer guides</h3>
      <div className="row spaced">{(d?.naicLinks || []).map((l, i) => (
        <a key={i} className="button secondary small" href={safeUrl(l.url)} target="_blank" rel="noopener noreferrer" data-testid={`naic-${i}`}>{l.label} ↗</a>))}</div>
      <p className="small muted spaced">General information only, not an offer, application, quote, or recommendation. An annuity is a separate insurance contract, not a life-insurance death benefit. <Link className="text-link" to="/life-insurance">Ask a question ↗</Link></p>
    </div>
  </>);
}

/* ------------------------------------------------------------------ about me */
function About() {
  const { site } = useSite();
  const a = site.about || {};
  const gallery = a.gallery || [];
  const connect = a.connect || [];
  return (<>
    <PageHead eyebrow={a.eyebrow} title={a.title} lead={a.intro} />
    <div className="wrap content-bottom about-page">
      <section className="section" id="story">
        <p className="eyebrow">{a.storyTitle}</p>
        <p className="preline lead" style={{ maxWidth: 760 }} data-testid="about-story">{a.story}</p>
      </section>
      <section className="section" id="photos">
        <p className="eyebrow">{a.photosTitle}</p>
        {a.photosNote && <p className="muted" style={{ maxWidth: 620 }}>{a.photosNote}</p>}
        <div className="gallery-grid spaced" data-testid="about-gallery">
          {gallery.length ? gallery.map((g, i) => (
            <EdImg k={`about.gallery.${i}.url`} label={`Gallery photo ${i + 1}`} as="figure" className="gallery-item" key={i}>
              <img src={mediaUrl(g.url) || g.url} alt={g.alt || g.caption || "Photo"} loading="lazy" />
              {g.caption && <figcaption className="small muted">{g.caption}</figcaption>}
            </EdImg>)) : <Empty>Photos coming soon.</Empty>}
        </div>
      </section>
      <section className="section" id="connect">
        <p className="eyebrow">{a.connectTitle}</p>
        {a.connectNote && <p className="muted" style={{ maxWidth: 620 }}>{a.connectNote}</p>}
        {connect.length ? <div className="row spaced">{connect.map((c, i) => (
          <SmartLink key={i} className="button secondary small" to={c.url} data-testid={`about-connect-${i}`}>{c.label} ↗</SmartLink>))}</div>
          : <p className="small muted spaced">Ways to connect are coming soon.</p>}
      </section>
      <div className="spaced" style={{ marginTop: 18 }}>
        <Link className="button" to="/book" data-testid="about-book-cta">Book a free consulting call <ArrowUpRight size={15} /></Link>
      </div>
    </div>
  </>);
}

/* ------------------------------------------------------------------ root */
function Shell() {
  const { site } = useSite();
  const loc = useLocation();
  useEditBridge();
  const area = currentArea(loc.pathname);
  useEffect(() => { applyAreaTheme(area, site.theme); }, [area, site.theme]);
  useRouteMeta(site, loc.pathname);
  const preview = isPreview();
  return (
    <>
      {!preview && (area === "social" ? <SocialHeader /> : <Header />)}
      <main id="main" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<PersonalHome />} />
          <Route path="/consulting" element={<ConsultingHome />} />
          <Route path="/life-insurance" element={<LifeInsurance />} />
          <Route path="/life-insurance/options" element={<InsuranceOptions />} />
          <Route path="/life-insurance/book" element={<InsuranceSchedule />} />
          <Route path="/about" element={<About />} />
          <Route path="/online" element={<Online />} />
          <Route path="/online/:slug" element={<SocialSlug />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:id" element={<Services />} />
          <Route path="/packages" element={<Navigate to="/services" replace />} />
          <Route path="/packages/*" element={<Navigate to="/services" replace />} />
          <Route path="/consulting/:slug" element={<CustomPage section="consulting" />} />
          <Route path="/life-insurance/:slug" element={<CustomPage section="insurance" />} />
          <Route path="/purchase/:id" element={<Purchase />} />
          <Route path="/start" element={<Start />} />
          <Route path="/book" element={<Book />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/admin" element={<OwnerDashboard />} />
          <Route path="/privacy" element={<Policy type="privacy" />} />
          <Route path="/terms" element={<Policy type="terms" />} />
          <Route path="/cancellation" element={<Policy type="cancellation" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!preview && (area === "social" ? <SocialFooter /> : <Footer />)}
    </>
  );
}

export default function App() {
  const [state, setState] = useState(null);
  const [me, setMe] = useState({ role: null });
  const reload = useCallback(async () => {
    const cfg = await apiGet(`/config${isDraft() ? "?draft=1" : ""}`);
    const meData = await apiGet("/me");
    setState(cfg); setMe(meData);
  }, []);
  useEffect(() => { reload().catch((e) => console.error(e)); }, [reload]);
  const value = useMemo(() => state ? { ...state, me, reload } : null, [state, me, reload]);
  if (!value) return <div className="loading">Getting things ready…</div>;
  return (
    <Ctx.Provider value={value}>
      <BrowserRouter>
        <a className="skip-link" href="#main">Skip to content</a>
        <Shell />
        <Toaster position="bottom-center" richColors />
      </BrowserRouter>
    </Ctx.Provider>
  );
}
